/**
 * SurgicalPanel — replaces SOAPPanel for specialty.mode === 'surgical'
 * ────────────────────────────────────────────────────────────────────
 * Three visit-type subsections toggled by a top segmented control:
 *   • Pre-op  — H&P summary, ASA class, METs, anesthesia plan
 *   • Post-op — POD auto-calc, wound status, global period banner
 *   • Procedure note — operative report (dictation-first surface)
 *
 * All data mocked. Global-period tracking uses a mock surgery date
 * so the banner updates naturally across days.
 */
import { useMemo, useState } from 'react';
import { Button } from '../../../ui/button';
import { Badge } from '../../../ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../../ui/tabs';
import {
  Scissors, Copy, ShieldAlert, CheckCircle2, Activity, Clock, Stethoscope, FileText, BadgeCheck,
} from 'lucide-react';
import { toast } from 'sonner';
import { CMS_GLOBAL_PERIODS, globalPeriodStatus } from '../../../../config/cmsGlobalPeriods';

/* Mock: patient had TKA 10 days ago so POD = 10 and global period active. */
const MOCK_SURGERY_DATE = (() => {
  const d = new Date(); d.setDate(d.getDate() - 10);
  return d.toISOString().slice(0, 10);
})();

const daysAgo = (iso) => Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);

const ASA_OPTIONS = [
  { v: 'I',   desc: 'Healthy' },
  { v: 'II',  desc: 'Mild systemic disease' },
  { v: 'III', desc: 'Severe systemic disease' },
  { v: 'IV',  desc: 'Life-threatening disease' },
  { v: 'V',   desc: 'Moribund' },
];

const Field = ({ label, children }) => (
  <div className="space-y-0.5">
    <p className="text-[9px] uppercase tracking-wide text-slate-400">{label}</p>
    {children}
  </div>
);

const PreOpView = () => {
  const [asa, setAsa] = useState('II');
  const [mets, setMets] = useState(6);
  const [anesthesia, setAnesthesia] = useState('General');
  return (
    <div className="space-y-3" data-testid="surg-preop">
      <section className="rounded-xl border border-slate-200 bg-white p-4">
        <header className="flex items-center gap-2 mb-3">
          <Stethoscope className="h-3.5 w-3.5 text-slate-500" />
          <p className="text-xs font-bold text-slate-800">Pre-op risk stratification</p>
        </header>
        <div className="grid grid-cols-3 gap-3">
          <Field label="ASA class">
            <div className="flex gap-1 flex-wrap">
              {ASA_OPTIONS.map((o) => (
                <button
                  key={o.v}
                  onClick={() => setAsa(o.v)}
                  className={`px-2 py-1 rounded-md text-[10px] font-semibold border transition ${
                    asa === o.v ? 'border-slate-900 bg-slate-900 text-white' : 'border-slate-200 bg-white text-slate-700 hover:border-slate-400'
                  }`}
                  data-testid={`surg-asa-${o.v}`}
                  title={o.desc}
                >
                  ASA {o.v}
                </button>
              ))}
            </div>
          </Field>
          <Field label="Functional capacity (METs)">
            <input
              type="number" min={1} max={15} value={mets}
              onChange={(e) => setMets(+e.target.value)}
              className="w-full text-[11px] px-2 py-1.5 rounded border border-slate-200 bg-slate-50/60 focus:bg-white focus:border-slate-400 outline-none"
              data-testid="surg-mets"
            />
            <p className="text-[9px] text-slate-400">{mets >= 4 ? 'Adequate (≥ 4)' : 'Below 4 — obtain cardiac clearance'}</p>
          </Field>
          <Field label="Anesthesia plan">
            <select
              value={anesthesia} onChange={(e) => setAnesthesia(e.target.value)}
              className="w-full text-[11px] px-2 py-1.5 rounded border border-slate-200 bg-slate-50/60 focus:bg-white focus:border-slate-400 outline-none"
              data-testid="surg-anesthesia"
            >
              {['General', 'Regional', 'Neuraxial', 'MAC', 'Local'].map((x) => <option key={x}>{x}</option>)}
            </select>
          </Field>
        </div>
      </section>

      <section className="rounded-xl border border-slate-200 bg-white p-4 text-[11px] text-slate-700 space-y-2">
        <header className="flex items-center gap-2 mb-1">
          <FileText className="h-3.5 w-3.5 text-slate-500" />
          <p className="text-xs font-bold text-slate-800">H&P summary</p>
          <Badge className="ml-auto text-[9px] bg-indigo-50 text-indigo-700 border-indigo-200">Auto-drafted</Badge>
        </header>
        <p>HPI: Right knee pain × 3 years, failed conservative mgmt incl. NSAIDs, PT, corticosteroid injection × 2.</p>
        <p>PMH: HTN (well-controlled), T2DM (A1c 6.8%). No prior cardiac events. Exercise tolerance: walks 3–4 blocks without SOB (≥ 4 METs).</p>
        <p>Labs today: CBC WNL, BMP WNL, INR 1.0, EKG: NSR 68.</p>
        <p>Consent discussed including risks of infection, bleeding, VTE, anesthesia complications, need for transfusion, revision surgery. Patient verbalized understanding.</p>
      </section>
    </div>
  );
};

const PostOpView = ({ cpt, surgeryDate, onCptChange, onDateChange }) => {
  const [wound, setWound] = useState('healing');
  const gp = globalPeriodStatus(surgeryDate, cpt);
  const rec = gp.rec;
  const pod = gp.elapsed ?? 0;

  const bannerTone = !rec
    ? 'slate'
    : rec.days == null
      ? 'indigo'             // XXX / YYY / ZZZ / MMM — special
      : gp.active
        ? 'amber'
        : 'emerald';
  const bannerCls = {
    slate:   'border-slate-200 bg-slate-50',
    indigo:  'border-indigo-200 bg-indigo-50/50',
    amber:   'border-amber-200 bg-amber-50/60',
    emerald: 'border-emerald-200 bg-emerald-50/40',
  }[bannerTone];
  const bannerText = !rec
    ? `CPT ${cpt || '—'} not found in the CMS curated table.`
    : rec.days == null
      ? `Global period (CMS ${rec.period}) — ${rec.period === 'MMM' ? 'maternity concept (special rule)' : rec.period === 'XXX' ? 'global concept does not apply' : rec.period === 'ZZZ' ? 'add-on code — inherits primary' : 'contractor-priced — varies'}.`
      : gp.active
        ? `Global period — ${gp.daysRemaining} days remaining`
        : 'Global period — expired';

  return (
    <div className="space-y-3" data-testid="surg-postop">
      {/* CPT + surgery-date pickers */}
      <section className="rounded-xl border border-slate-200 bg-white p-3 grid grid-cols-3 gap-3">
        <Field label="Primary procedure CPT">
          <select
            value={cpt}
            onChange={(e) => onCptChange(e.target.value)}
            className="w-full text-[11px] px-2 py-1.5 rounded border border-slate-200 bg-slate-50/60 focus:bg-white focus:border-slate-400 outline-none"
            data-testid="surg-cpt-select"
          >
            {CMS_GLOBAL_PERIODS.map((c) => (
              <option key={c.code} value={c.code}>
                {c.code} — {c.desc} · {c.period}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Surgery date">
          <input
            type="date" value={surgeryDate}
            onChange={(e) => onDateChange(e.target.value)}
            className="w-full text-[11px] px-2 py-1.5 rounded border border-slate-200 bg-slate-50/60 focus:bg-white focus:border-slate-400 outline-none"
            data-testid="surg-date-input"
          />
        </Field>
        <Field label="Post-op day">
          <p className="text-[12px] font-bold text-slate-900 mt-1 tabular-nums">POD {pod}</p>
        </Field>
      </section>

      {/* Global-period banner (CMS-driven) */}
      <div
        className={`rounded-xl border p-3 flex items-center gap-3 ${bannerCls}`}
        data-testid="surg-global-banner"
      >
        <BadgeCheck className={`h-4 w-4 ${bannerTone === 'amber' ? 'text-amber-700' : bannerTone === 'emerald' ? 'text-emerald-700' : bannerTone === 'indigo' ? 'text-indigo-700' : 'text-slate-500'}`} />
        <div className="flex-1 min-w-0">
          <p className="text-[11px] font-semibold text-slate-900">{bannerText}</p>
          <p className="text-[10px] text-slate-500">
            {rec
              ? `Surgery ${surgeryDate} · CPT ${rec.code} "${rec.desc}" · CMS global indicator ${rec.period}${rec.days != null ? ` (${rec.days} days)` : ''}. ${gp.active ? 'Related E/M is bundled; use modifier 24 if unrelated.' : ''}`
              : 'Add this code to cmsGlobalPeriods.js or load the full CMS PFSRVU file.'}
          </p>
        </div>
        <span className={`text-[9px] px-2 py-0.5 rounded-full font-semibold ${
          bannerTone === 'amber'   ? 'bg-amber-100 text-amber-800'
          : bannerTone === 'emerald' ? 'bg-emerald-100 text-emerald-800'
          : bannerTone === 'indigo'  ? 'bg-indigo-100 text-indigo-800'
          : 'bg-slate-100 text-slate-600'
        }`}>
          {!rec ? 'unknown' : rec.days == null ? rec.period : gp.active ? 'Active' : 'Closed'}
        </span>
      </div>

      <section className="rounded-xl border border-slate-200 bg-white p-4">
        <header className="flex items-center gap-2 mb-3">
          <Clock className="h-3.5 w-3.5 text-slate-500" />
          <p className="text-xs font-bold text-slate-800">Post-op status</p>
          <Badge className="ml-auto text-[9px] bg-slate-100 text-slate-600 border-0">POD {pod}</Badge>
        </header>
        <div className="grid grid-cols-3 gap-3">
          <Field label="Wound status">
            <select
              value={wound} onChange={(e) => setWound(e.target.value)}
              className="w-full text-[11px] px-2 py-1.5 rounded border border-slate-200 bg-slate-50/60 focus:bg-white focus:border-slate-400 outline-none"
              data-testid="surg-wound"
            >
              {['healing', 'erythematous', 'dehiscence', 'purulent', 'fully healed'].map((x) => <option key={x}>{x}</option>)}
            </select>
          </Field>
          <Field label="Pain (0–10)">
            <input type="number" min={0} max={10} defaultValue={3}
              className="w-full text-[11px] px-2 py-1.5 rounded border border-slate-200 bg-slate-50/60 focus:bg-white focus:border-slate-400 outline-none" />
          </Field>
          <Field label="ROM (flexion)">
            <input type="number" min={0} max={150} defaultValue={95}
              className="w-full text-[11px] px-2 py-1.5 rounded border border-slate-200 bg-slate-50/60 focus:bg-white focus:border-slate-400 outline-none" />
          </Field>
        </div>
        <p className="mt-2 text-[10px] text-slate-500">
          Checklist: <span className="text-emerald-700">DVT ppx ✓</span> · <span className="text-emerald-700">Staple removal schedule ✓</span> · <span className="text-amber-700">PT referral pending</span>
        </p>
      </section>
    </div>
  );
};

const ProcedureView = () => {
  const [sections, setSections] = useState({
    indication:  'Medial compartment end-stage osteoarthritis of the right knee, refractory to conservative mgmt.',
    procedure:   'Right total knee arthroplasty (CPT 27447).',
    findings:    'Grade IV chondromalacia of medial compartment; intact PCL; menisci debrided.',
    technique:   'Standard midline approach, medial parapatellar arthrotomy. Femoral, tibial, and patellar cuts per technique guide. Cemented components implanted. Trial range of motion 0–125° without impingement. Wound closed in layers with barbed sutures.',
    ebl:         '150 mL',
    complications:'None.',
    disposition: 'To PACU in stable condition.',
  });
  return (
    <div className="space-y-3" data-testid="surg-procedure">
      <section className="rounded-xl border border-slate-200 bg-white p-4 space-y-3">
        <header className="flex items-center gap-2">
          <FileText className="h-3.5 w-3.5 text-slate-500" />
          <p className="text-xs font-bold text-slate-800">Operative report</p>
          <Badge className="ml-auto text-[9px] bg-indigo-50 text-indigo-700 border-indigo-200">Dictation-first</Badge>
        </header>
        {Object.entries(sections).map(([k, v]) => (
          <div key={k} className="space-y-0.5">
            <p className="text-[9px] uppercase tracking-wide text-slate-400">{k.replace(/_/g, ' ')}</p>
            <textarea
              value={v} rows={k === 'technique' ? 4 : 2}
              onChange={(e) => setSections({ ...sections, [k]: e.target.value })}
              className="w-full text-[11px] px-3 py-2 rounded-lg border border-slate-200 bg-slate-50/60 focus:bg-white focus:border-slate-400 outline-none"
              data-testid={`surg-proc-${k}`}
            />
          </div>
        ))}
      </section>
    </div>
  );
};

export const SurgicalPanel = ({ patient, onDone }) => {
  const [visitType, setVisitType] = useState('preop'); // 'preop' | 'postop' | 'procedure'
  const [cpt, setCpt] = useState('27447');
  const [surgeryDate, setSurgeryDate] = useState(MOCK_SURGERY_DATE);

  return (
    <div className="space-y-3" data-testid="surgical-panel">
      {/* Action bar */}
      <div className="bg-slate-900 rounded-xl p-3 flex items-center gap-2">
        <Button size="sm" onClick={() => toast.success('Note copied')} className="bg-white text-slate-900 hover:bg-slate-100 gap-1.5 font-semibold h-8 text-xs">
          <Copy className="h-3 w-3" /> Copy Full Note
        </Button>
        <div className="flex-1" />
        <Badge className="bg-white/15 text-white border-0 text-[10px] gap-1">
          <Scissors className="h-2.5 w-2.5" /> Surgical Mode
        </Badge>
        <Button size="sm" variant="ghost" className="text-xs text-slate-300 hover:bg-white/10 h-7" onClick={onDone} data-testid="surg-done-btn">Done</Button>
      </div>

      {/* Visit-type segmented toggle */}
      <div className="inline-flex rounded-full bg-slate-100 p-1" data-testid="surg-visit-toggle">
        {[
          { id: 'preop',     label: 'Pre-op',          icon: Activity },
          { id: 'postop',    label: 'Post-op',         icon: CheckCircle2 },
          { id: 'procedure', label: 'Procedure note',  icon: FileText },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => setVisitType(t.id)}
            className={`px-3 py-1 text-[11px] font-semibold inline-flex items-center gap-1.5 rounded-full transition ${
              visitType === t.id ? 'bg-slate-900 text-white shadow-sm' : 'text-slate-600 hover:text-slate-900'
            }`}
            data-testid={`surg-toggle-${t.id}`}
          >
            <t.icon className="h-3 w-3" /> {t.label}
          </button>
        ))}
      </div>

      {visitType === 'preop'     && <PreOpView />}
      {visitType === 'postop'    && (
        <PostOpView
          cpt={cpt}
          surgeryDate={surgeryDate}
          onCptChange={setCpt}
          onDateChange={setSurgeryDate}
        />
      )}
      {visitType === 'procedure' && <ProcedureView />}
    </div>
  );
};

export default SurgicalPanel;
